/**
 * `qf.geometry` shapes as chart drawings — the second `derived` source.
 *
 * The server's detectors send shapes (`@qufolio/transport`'s `geometry.ts`);
 * this turns the ones a chart's `geometry` plots asked for into the contract's
 * `Annotation` shape, so the renderer draws them through the same path as the
 * trading plan's levels (`plan-annotations.ts`). Design:
 * `docs/plans/chart-patterns.md`, step 5.
 *
 * ## Derived, so never the user's
 *
 * Rebuilt from the latest response on every render and handed to
 * `setAnnotations` as `derived` — never written into `Pane.annotations`. The
 * renderer gives a derived drawing no grips unless its id is in the editable
 * set (only plan levels are), never reports it as selected, and the ⌫ clear
 * skips it. Removing or hiding the plot is the only handle, and the only one
 * there should be: a hand-moved trendline would be a line the screener never
 * tested.
 *
 * ## What a trendline draws (owner, 2026-10-04)
 *
 * - **One line per trendline**, first pivot to its end — the break bar, or the
 *   last bar if it never broke. Support takes `positive`, resistance
 *   `negative`, at the plot's width.
 * - **Broken: dashed and faded**, ending at the break.
 * - **Active: a stub past the last bar**, dashed in the forward-projection
 *   yellow (`warn`), {@link STUB_BARS} bars long. Extended by *bars*, along the
 *   detector's own slope per bar, and placed in time with `timeAtOffset` — so on
 *   a gapped axis it stays on the line.
 * - **No touch dots** — the touches travel in the payload and are not drawn.
 *
 * Ids are `geo|<plotId>|<shapeId>` — synthetic and not ULIDs, which is
 * `plan-annotations.ts`' argument: an accidental save then fails validation
 * loudly rather than storing a line the server owns.
 */

import { indexAtTime, timeAtOffset } from "@qufolio/chart-engine";
import type { Annotation, Plot } from "@qufolio/model";
import type { GeometryPayload, GeometryShape } from "@qufolio/transport";

/**
 * How far an active line's stub reaches past the last bar, in bars.
 *
 * MarketMRIPro's own extension length (`MRIChartHelper.AddTrendlineAnnotations`,
 * 20 bars past P2) — the owner's prior choice, kept until they say otherwise.
 */
export const STUB_BARS = 20;

/** Theme token per role. A role this table does not know falls back to the plot's own stroke. */
const ROLE_TOKEN: Record<string, string> = {
  support: "positive",
  resistance: "negative",
  outline: "textSecondary",
  rim: "accent",
  neckline: "accent",
  target: "warn",
  // Box (2026-10-11): the Darvas rectangle and the flat base's buy point in
  // accent, Darvas's stop under a forming box in negative, the base low muted.
  box: "accent",
  pivot: "accent",
  stop: "negative",
  baseLow: "textSecondary",
  // seasHits (2026-10-22): a year up, a year down; up − down in orange and the
  // excess version (beat their own year − did not) in purple — yellow did not
  // read against the stacks (owner).
  up: "positive",
  down: "negative",
  net: "accent.orange",
  xnet: "accent.purple",
};

const BROKEN_DASH = [6, 4];
const BROKEN_OPACITY = 0.5;
const FORMING_DASH = [6, 4];
const FAILED_OPACITY = 0.4;
const TARGET_DASH = [4, 3];
const STUB_DASH = [2, 3];
const STUB_TOKEN = "warn";
/** A plot with no width of its own — `add-indicator.ts`' `PATTERN_THICKNESS`. */
const DEFAULT_THICKNESS = 3;
/** `seasHits`' lines — 8 by default (owner, 2026-10-23), the user's to change. */
const HITS_THICKNESS = 8;
/** How far either side of its bar a one-bar block reaches, in bars: 0.7 of a bar drawn, 0.3 gap. */
const SINGLE_BAR_REACH = 0.35;

/** The id a shape's drawing carries — one place, so the format cannot drift. */
export const geometryAnnotationId = (plotId: string, shapeId: string, part = ""): string =>
  `geo|${plotId}|${shapeId}${part ? `|${part}` : ""}`;

/**
 * Every drawing one pane's `geometry` plots ask for.
 *
 * `barTimes` are the chart's loaded bars, epoch seconds ascending — needed only
 * for the stub, whose far end has no bar and is extrapolated from the last.
 */
export function geometryAnnotations(
  geometry: GeometryPayload | null | undefined,
  plots: readonly Plot[] | undefined,
  barTimes: readonly number[],
): Annotation[] {
  if (!geometry || !plots) return [];
  const out: Annotation[] = [];
  for (const plot of plots) {
    if (plot.type !== "geometry" || plot.hidden || !plot.outputAlias) continue;
    for (const shape of geometry.sets[plot.outputAlias] ?? []) {
      out.push(...shapeAnnotations(plot, shape, barTimes));
    }
  }
  return out;
}

/**
 * How a shape's status draws, the same for every primitive in it.
 *
 * A trendline is `active` (solid) or `broken` (dashed, faded). A pattern is
 * `forming` (dashed — not yet true), `confirmed` (solid) or `failed` (dashed
 * and faded, and only drawn when the plot asks for failed ones).
 */
function statusStyle(shape: GeometryShape): { dash?: number[]; opacity?: number } {
  switch (shape.status) {
    case "broken":
      return { dash: BROKEN_DASH, opacity: BROKEN_OPACITY };
    case "forming":
      return { dash: FORMING_DASH };
    case "failed":
      return { dash: FORMING_DASH, opacity: FAILED_OPACITY };
    default:
      return {};
  }
}

function shapeAnnotations(plot: Plot, shape: GeometryShape, barTimes: readonly number[]): Annotation[] {
  const out: Annotation[] = [];
  const thickness = plot.style?.thickness ?? (shape.kind === "seasHits" ? HITS_THICKNESS : DEFAULT_THICKNESS);
  const status = statusStyle(shape);
  const tokenFor = (role: string | undefined) =>
    (role && ROLE_TOKEN[role]) ?? plot.style?.stroke ?? "series1";
  for (const [i, primitive] of shape.primitives.entries()) {
    const id = (part = "") => geometryAnnotationId(plot.id, shape.id, `${i}${part}`);
    switch (primitive.t) {
      case "segment": {
        out.push({
          id: id(),
          type: "line",
          x1: primitive.a.t,
          y1: primitive.a.p,
          x2: primitive.b.t,
          y2: primitive.b.p,
          style: { stroke: tokenFor(primitive.role), thickness, ...status },
        } as Annotation);
        // Only a trendline carries a stub — a pattern's handle channel stops
        // at the handle.
        const stub =
          shape.kind === "trendline" && shape.status !== "broken"
            ? stubEnd(primitive.b, primitive.slopePerBar, barTimes)
            : undefined;
        if (stub) {
          out.push({
            id: id("|stub"),
            type: "line",
            x1: primitive.b.t,
            y1: primitive.b.p,
            x2: stub.t,
            y2: stub.p,
            style: { stroke: STUB_TOKEN, thickness, dash: STUB_DASH },
          } as Annotation);
        }
        break;
      }
      case "path":
        // The cup's fitted U, one point per bar: a polyline that reads as a curve.
        if (primitive.points.length >= 2) {
          out.push({
            id: id(),
            type: "polyline",
            points: primitive.points.map((p) => ({ x: p.t, y: p.p })),
            style: { stroke: tokenFor(primitive.role), thickness, ...status },
          } as Annotation);
        }
        break;
      case "level": {
        const ends = levelEnds(primitive, barTimes);
        if (!ends) break;
        out.push({
          id: id(),
          type: "line",
          x1: ends.x1,
          y1: primitive.price,
          x2: ends.x2,
          y2: primitive.price,
          style: { stroke: tokenFor(primitive.role), thickness, ...status },
          // A block covers its bars to their slot edges, the future rows the
          // server added included: placed at exactly those times, never snapped
          // to a bar — snapping makes a one-bar block zero wide.
          ...(primitive.edges ? { exactX: true } : {}),
        } as Annotation);
        break;
      }
      case "target": {
        // A flat line at the target price, from the breakout bar a fixed number
        // of bars on — past the last bar when it is recent, which only a derived
        // line may do.
        const bars = primitive.bars ?? STUB_BARS;
        const index = indexAtTime(barTimes, primitive.from.t);
        const end = index === undefined ? undefined : timeAtOffset(barTimes, index + bars);
        if (end !== undefined) {
          out.push({
            id: id(),
            type: "line",
            x1: primitive.from.t,
            y1: primitive.price,
            x2: end,
            y2: primitive.price,
            style: { stroke: tokenFor(primitive.role ?? "target"), thickness, dash: TARGET_DASH },
          } as Annotation);
        }
        break;
      }
      default:
        break; // a label — none drawn (owner: no labels on cup and handle)
    }
  }
  return out;
}

/**
 * Where a level runs. Plain: bar time to bar time. With `edges` (a `seasHits`
 * block) it covers its bars wholly — the first bar's left slot edge to the last
 * bar's right — so a week on a daily chart is as wide as the week. An end past
 * the last bar is placed `aheadFrom`/`aheadTo` bars on, as a span is.
 */
function levelEnds(
  level: { from: number; to?: number; edges?: boolean; aheadFrom?: number; aheadTo?: number },
  barTimes: readonly number[],
): { x1: number; x2: number } | undefined {
  const to = level.to ?? level.from;
  if (!level.edges) return { x1: level.from, x2: to };
  const last = barTimes.length - 1;
  if (last < 1) return undefined;
  // A block of several bars (a week, a month) runs first bar to last bar, so
  // two neighbours keep a **whole bar** of gap at any zoom ("I need gap between
  // week"); a one-bar block (Basis D) reaches `SINGLE_BAR_REACH` of a bar either
  // side of its bar, leaving the rest as gap.
  const i1 = level.aheadFrom !== undefined ? last + level.aheadFrom : indexAtTime(barTimes, level.from);
  const i2 = level.aheadTo !== undefined ? last + level.aheadTo : indexAtTime(barTimes, to);
  if (i1 === undefined || i2 === undefined) return undefined;
  const reach = i2 > i1 ? 0 : SINGLE_BAR_REACH;
  const x1 = axisTimeAt(barTimes, i1 - reach);
  const x2 = axisTimeAt(barTimes, i2 + reach);
  if (x1 === undefined || x2 === undefined || !(x2 > x1)) return undefined;
  return { x1, x2 };
}

/**
 * The time the X axis draws at a fractional bar position.
 *
 * **Not `timeAtOffset`, and the weekend is why** (owner's screenshots,
 * 2026-10-23). The axis runs time at one *bar unit* per bar — a day on a daily
 * chart — and drops the gaps: Friday + 1 day already sits on Monday, and every
 * time from Saturday to Monday lands there too. So a point `f` of a bar past
 * bar `i` is `time[i] + f × unit`, and a point just *before* a bar is measured
 * forward from the bar before it. Interpolating by calendar time instead put a
 * Friday's right end on Monday and cut Monday's left half off.
 */
export function axisTimeAt(times: readonly number[], offset: number): number | undefined {
  const last = times.length - 1;
  if (last < 1) return undefined;
  const unit = barUnit(times);
  const i = Math.max(0, Math.min(last, Math.floor(offset)));
  return times[i]! + (offset - i) * unit;
}

/** The axis' bar unit: the smallest step between neighbouring bars (a day, a week…). */
function barUnit(times: readonly number[]): number {
  let unit = Infinity;
  const from = Math.max(1, times.length - 60);
  for (let i = from; i < times.length; i++) {
    const step = times[i]! - times[i - 1]!;
    if (step > 0 && step < unit) unit = step;
  }
  return Number.isFinite(unit) ? unit : 86_400;
}

/**
 * Where the stub ends: {@link STUB_BARS} bars after `from`, along `slopePerBar`.
 *
 * `undefined` when `from` is not a loaded bar — a stub cannot start from a
 * point the chart does not have — or when there is no slope to follow.
 */
function stubEnd(
  from: { t: number; p: number },
  slopePerBar: number | undefined,
  barTimes: readonly number[],
): { t: number; p: number } | undefined {
  if (slopePerBar === undefined || !Number.isFinite(slopePerBar)) return undefined;
  const index = indexAtTime(barTimes, from.t);
  if (index === undefined) return undefined;
  const t = timeAtOffset(barTimes, index + STUB_BARS);
  if (t === undefined) return undefined;
  return { t, p: from.p + slopePerBar * STUB_BARS };
}
